import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  endOfMonth,
  endOfQuarter,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';

export const IST_TZ = 'Asia/Kolkata';

export type PeriodKey =
  | 'today'
  | 'yesterday'
  | '7d'
  | '28d'
  | 'last_month'
  | 'last_year'
  | 'month'
  | 'quarter'
  | 'custom';
export type CompareMode = 'previous' | 'last-year';

export interface DateRange {
  start: Date; // UTC instants representing IST midnight boundaries
  end: Date; // exclusive
  label: string;
}

export interface Metric {
  current: number;
  previous: number;
  delta: number;
  deltaPct: number;
  /** False when previous=0 and current>0 — comparison is not meaningful. */
  comparable: boolean;
}

function istNow(): Date {
  return toZonedTime(new Date(), IST_TZ);
}

function asUtc(zonedDate: Date): Date {
  return fromZonedTime(zonedDate, IST_TZ);
}

function dayRange(zonedDay: Date): DateRange {
  const startZoned = startOfDay(zonedDay);
  const endZoned = startOfDay(addDays(zonedDay, 1));
  return {
    start: asUtc(startZoned),
    end: asUtc(endZoned),
    label: 'day',
  };
}

export function getPeriodRange(period: PeriodKey, custom?: { start: Date; end: Date }): DateRange {
  const now = istNow();
  switch (period) {
    case 'today':
      return { ...dayRange(now), label: 'Today' };
    case 'yesterday':
      return { ...dayRange(subDays(now, 1)), label: 'Yesterday' };
    case '7d': {
      const endZoned = startOfDay(now);
      const startZoned = subDays(endZoned, 7);
      return { start: asUtc(startZoned), end: asUtc(endZoned), label: 'Last 7 days' };
    }
    case '28d': {
      const endZoned = startOfDay(now);
      const startZoned = subDays(endZoned, 28);
      return { start: asUtc(startZoned), end: asUtc(endZoned), label: 'Last 28 days' };
    }
    case 'last_month': {
      const lastMonth = subMonths(now, 1);
      const startZoned = startOfMonth(lastMonth);
      const endZoned = startOfDay(addDays(endOfMonth(lastMonth), 1));
      return { start: asUtc(startZoned), end: asUtc(endZoned), label: 'Last month' };
    }
    case 'last_year': {
      // Full previous calendar year — Jan 1 to Dec 31.
      const prevYear = now.getFullYear() - 1;
      const startZoned = startOfDay(new Date(prevYear, 0, 1));
      const endZoned = startOfDay(new Date(prevYear + 1, 0, 1));
      return { start: asUtc(startZoned), end: asUtc(endZoned), label: 'Last year' };
    }
    case 'month': {
      const startZoned = startOfMonth(now);
      const endZoned = startOfDay(addDays(endOfMonth(now), 1));
      return { start: asUtc(startZoned), end: asUtc(endZoned), label: 'This month' };
    }
    case 'quarter': {
      const startZoned = startOfQuarter(now);
      const endZoned = startOfDay(addDays(endOfQuarter(now), 1));
      return { start: asUtc(startZoned), end: asUtc(endZoned), label: 'This quarter' };
    }
    case 'custom': {
      if (!custom) throw new Error('custom period requires range');
      return {
        start: asUtc(startOfDay(toZonedTime(custom.start, IST_TZ))),
        end: asUtc(startOfDay(addDays(toZonedTime(custom.end, IST_TZ), 1))),
        label: 'Custom',
      };
    }
  }
}

/**
 * Resolve a period + optional custom range into a single DateRange.
 * When period === 'custom' and customRange provided, use that. Otherwise,
 * fall back to a default range (7d) — same legacy behaviour as the
 * `period === 'custom' ? '7d' : period` pattern this replaces.
 */
export function effectivePeriodRange(
  period: PeriodKey,
  customRange?: { start: string; end: string } | null,
): DateRange {
  if (period === 'custom' && customRange) {
    const start = new Date(`${customRange.start}T00:00:00`);
    const end = new Date(`${customRange.end}T00:00:00`);
    return getPeriodRange('custom', { start, end });
  }
  return getPeriodRange(period === 'custom' ? '7d' : period);
}

export function getCompareRange(range: DateRange, mode: CompareMode = 'previous'): DateRange {
  if (mode === 'last-year') {
    return {
      start: asUtc(subYears(toZonedTime(range.start, IST_TZ), 1)),
      end: asUtc(subYears(toZonedTime(range.end, IST_TZ), 1)),
      label: `${range.label} last year`,
    };
  }
  const lenDays = Math.max(
    1,
    differenceInCalendarDays(toZonedTime(range.end, IST_TZ), toZonedTime(range.start, IST_TZ)),
  );
  const previousEndZoned = toZonedTime(range.start, IST_TZ);
  const previousStartZoned = subDays(previousEndZoned, lenDays);
  return {
    start: asUtc(previousStartZoned),
    end: asUtc(previousEndZoned),
    label: `Previous ${lenDays}d`,
  };
}

/**
 * Yesterday compared to the same date one month ago (clamped to last day of that month).
 */
export function getSameDayLastMonth(): { current: DateRange; previous: DateRange } {
  const now = istNow();
  const yesterday = subDays(startOfDay(now), 1);
  const lastMonthSameDay = (() => {
    const candidate = subMonths(yesterday, 1);
    const lastDayOfThatMonth = endOfMonth(candidate);
    return candidate.getTime() > lastDayOfThatMonth.getTime() ? lastDayOfThatMonth : candidate;
  })();
  return {
    current: { ...dayRange(yesterday), label: 'Yesterday' },
    previous: { ...dayRange(lastMonthSameDay), label: 'Same day last month' },
  };
}

/**
 * Yesterday compared to the same weekday a week earlier.
 */
export function getSameDayLastWeek(): { current: DateRange; previous: DateRange } {
  const now = istNow();
  const yesterday = subDays(startOfDay(now), 1);
  const lastWeek = subWeeks(yesterday, 1);
  return {
    current: { ...dayRange(yesterday), label: 'Yesterday' },
    previous: { ...dayRange(lastWeek), label: 'Same day last week' },
  };
}

export function computeMetric(current: number, previous: number): Metric {
  const delta = current - previous;
  const comparable = !(previous === 0 && current !== 0);
  const deltaPct =
    previous === 0 ? (current === 0 ? 0 : 0) : (delta / Math.abs(previous)) * 100;
  return { current, previous, delta, deltaPct, comparable };
}

/** ISO date (YYYY-MM-DD) interpreted in IST for SQL `date` columns. */
export function toISTDateString(d: Date): string {
  const zoned = toZonedTime(d, IST_TZ);
  return [
    zoned.getFullYear(),
    String(zoned.getMonth() + 1).padStart(2, '0'),
    String(zoned.getDate()).padStart(2, '0'),
  ].join('-');
}

// Re-exports for convenience in tests / Edge Functions
export { addDays, addMonths, addWeeks, subDays, subMonths, subWeeks, startOfDay, startOfMonth, startOfQuarter };
